import React, { memo } from 'react'
import Box from 'transfix-ui/core/Box'
import Typography from 'transfix-ui/core/Typography'
import { IShipmentMissingInfo, IStops } from 'types/FMSGraphqlTypes'
import { TAB } from 'apps/fleet-planner/constants/loads'
import { TableCell, TableRow, Tooltip } from 'transfix-ui'
import { Documents } from './Documents'
import { Drivers } from './Drivers'
import { StopDateTime } from './StopDateTime'
import { Trailer } from './Trailer'
import { Truck } from './Truck'
import { IShipmentFragment } from './graphql/ShipmentFragment'
import {
  SHOW_DOCS_TABS,
  TRACKING_STATUS_TO_ICON,
  TRACKING_STATUS_TO_TOOLTIP,
  getPodState,
  getTrackingStatus,
} from './utils'

interface ILoadsTableRowProps {
  currentTab: string
  shipment: IShipmentFragment
}

export const LoadsTableRow = memo<ILoadsTableRowProps>(({ currentTab, shipment }) => {
  const {
    customer,
    drivers,
    etaObject,
    id,
    isExternal,
    marketplaceId,
    missingInfo,
    mpPodState,
    podUploaded,
    status,
    stops,
    trackingSummary,
    trailer,
    truck,
    vin,
  } = shipment || {}
  const { name: customerName } = customer || {}
  // TODO: replace with added isExternal field
  const { onTime = false } = etaObject || {}
  const { isTracking = false, shouldBeTracking = false } = trackingSummary || {}

  const { truckNumber } = truck || {}
  const { number: trailerNumber } = trailer || {}

  const trailerMissing = isExternal && !!missingInfo?.includes(IShipmentMissingInfo.TrailerMissing)
  const truckMissing = isExternal && !!missingInfo?.includes(IShipmentMissingInfo.TruckMissing)
  const podState = getPodState(podUploaded, mpPodState, missingInfo, isExternal)

  const [firstPickup] = stops || []
  const { city: pickupCity, stateCode: pickupStateCode } = firstPickup || {}

  const lastDelivery = stops[stops.length - 1]
  const { city: deliveryCity, stateCode: deliveryStateCode } = lastDelivery || {}

  const trackingStatus = getTrackingStatus(isTracking, onTime, shouldBeTracking, status)
  const TrackingIcon = TRACKING_STATUS_TO_ICON[trackingStatus]
  const trackingTooltip = TRACKING_STATUS_TO_TOOLTIP[trackingStatus]

  return (
    <TableRow key={id}>
      <TableCell>
        <Box display='flex' alignItems='center'>
          {isExternal && (
            <Tooltip
              position='right'
              type='dark'
              effect='solid'
              trigger={<TrackingIcon size='xlarge' />}>
              {trackingTooltip}
            </Tooltip>
          )}
          <Box marginLeft={isExternal ? '20px' : '44px'}>
            <Typography variant='body2' component='div'>
              {marketplaceId}
            </Typography>
            <Typography variant='body2' component='div'>
              {customerName}
            </Typography>
          </Box>
        </Box>
      </TableCell>
      <TableCell>
        <Typography variant='body2' component='div'>
          {pickupCity}, {pickupStateCode}
        </Typography>
        <Typography variant='body2' component='div'>
          <StopDateTime stop={firstPickup as IStops} />
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant='body2' component='div'>
          {deliveryCity}, {deliveryStateCode}
        </Typography>
        <Typography variant='body2' component='div'>
          <StopDateTime stop={lastDelivery as IStops} />
        </Typography>
      </TableCell>
      <TableCell>
        <Drivers drivers={drivers} isExternal={isExternal} />
      </TableCell>
      <TableCell>
        <Truck truckNumber={truckNumber} vin={vin} truckNeeded={truckMissing} />
      </TableCell>
      <TableCell>
        <Trailer trailerNumber={trailerNumber} trailerNeeded={trailerMissing} />
      </TableCell>
      <TableCell>{status}</TableCell>
      {SHOW_DOCS_TABS.includes(currentTab as TAB) && (
        <TableCell>
          <Documents podState={podState} />
        </TableCell>
      )}
    </TableRow>
  )
})
